import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { BANNER_CACHE_KEYS } from "./keys";

import { BannerRepository } from "@/repositories/banner";
import { Banner } from "@/types";

interface ReorderBannersContext {
  previousBanners: Banner[] | undefined;
}

export const useReorderBanners = () => {
  const queryClient = useQueryClient();
  const bannerRepository = useRef(new BannerRepository());

  return useMutation<Banner[], Error, Banner[], ReorderBannersContext>({
    mutationFn: (banners: Banner[]) =>
      bannerRepository.current.reorder({
        ids: banners.map((banner) => banner.id),
      }),
    onMutate: async (banners) => {
      await queryClient.cancelQueries({ queryKey: BANNER_CACHE_KEYS.LIST });

      const previousBanners = queryClient.getQueryData<Banner[]>(
        BANNER_CACHE_KEYS.LIST,
      );

      queryClient.setQueryData<Banner[]>(BANNER_CACHE_KEYS.LIST, banners);

      return { previousBanners };
    },
    onError: (_error, _banners, context) => {
      queryClient.setQueryData<Banner[]>(
        BANNER_CACHE_KEYS.LIST,
        context?.previousBanners,
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: BANNER_CACHE_KEYS.LIST }),
  });
};
